import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MapPin, Printer, Share2, Linkedin, LinkIcon, Check } from 'lucide-react';

const JobDetailPage = ({ jobs, loading, onApply }) => {
  const { jobId } = useParams();
  const [linkCopied, setLinkCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const job = Array.isArray(jobs)
    ? jobs.find((item) => String(item.id) === String(jobId) || String(item.job_code) === String(jobId))
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [jobId]);

  if (loading) {
    return (
      <div className="pt-28 pb-24 min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 flex justify-center">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="pt-28 pb-24 min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-black text-primary mb-3">Job Not Found</h2>
          <p className="text-slate-600 mb-6">This position may have been removed or is no longer available.</p>
          <Link to="/careers" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white font-bold">
            Back to all jobs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const postDate = job.created_at
    ? new Date(job.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
    : '';

  const jobPageUrl = `${window.location.origin}/careers/${job.id}`;
  const ogDescription = [
    job.location ? `Location: ${job.location}` : '',
    job.type ? `Type: ${job.type}` : '',
    job.experience ? `Experience: ${job.experience}` : '',
    job.description ? job.description.substring(0, 150) : ''
  ].filter(Boolean).join(' | ');

  return (
    <>
    <Helmet>
      <title>{`${job.title} - Careers at Libsys`}</title>
      <meta property="og:title" content={`${job.title} - Libsys Careers`} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={jobPageUrl} />
      <meta property="og:type" content="website" />
      <meta name="description" content={ogDescription} />
    </Helmet>
    <div className="pt-24 pb-20 min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
          <div className="lg:col-span-8 p-8 lg:p-10 border-r border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <Link to="/careers" className="text-primary text-sm font-bold hover:underline">
                &lt;&lt; Back to all jobs
              </Link>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="text-slate-500 hover:text-primary text-xs font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                  {showShareMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowShareMenu(false)} />
                      <div className="absolute right-0 top-8 z-20 bg-white rounded-xl shadow-xl border border-slate-200 py-2 w-56">
                        <a
                          href={(() => {
                            const lines = [
                              `We're hiring! ${job.title} at Libsys`,
                              '',
                              job.location ? `Location: ${job.location}` : '',
                              job.type ? `Type: ${job.type}` : '',
                              job.work_model ? `Work Model: ${job.work_model}` : '',
                              job.experience ? `Experience: ${job.experience}` : '',
                              '',
                              `Apply here: ${window.location.href}`,
                              '',
                              '#hiring #jobs #careers'
                            ].filter(Boolean).join('\n');
                            return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(lines)}`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-sm font-bold text-slate-700"
                          onClick={() => setShowShareMenu(false)}
                        >
                          <Linkedin className="w-4 h-4 text-[#0A66C2]" />
                          Share on LinkedIn
                        </a>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            setLinkCopied(true);
                            setShowShareMenu(false);
                            setTimeout(() => setLinkCopied(false), 2000);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-sm font-bold text-slate-700"
                        >
                          {linkCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <LinkIcon className="w-4 h-4 text-slate-400" />}
                          {linkCopied ? 'Link Copied!' : 'Copy Link'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <button onClick={() => window.print()} className="text-slate-500 hover:text-primary text-xs font-bold uppercase tracking-[0.14em] inline-flex items-center gap-1.5 transition-colors">
                  <Printer className="w-3.5 h-3.5" /> Print
                </button>
              </div>
            </div>

            <h1 className="text-3xl font-black text-primary mb-2">{job.title}</h1>
            <p className="text-slate-500 text-sm font-medium inline-flex items-center gap-1.5 mb-6">
              <MapPin className="w-4 h-4" /> {job.location || 'United States'} {postDate ? `| ${postDate}` : ''}
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              <button
                onClick={() => onApply(job)}
                className="px-7 py-3 rounded-full bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-sm font-black inline-flex items-center gap-2"
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-base font-black text-slate-800 mb-4">Job Description</h3>
              <div className="text-slate-700 text-[15px] leading-relaxed font-medium whitespace-pre-wrap">
                {job.description}
              </div>
            </div>

            {job.responsibilities ? (
              <div className="pt-8">
                <h3 className="text-base font-black text-slate-800 mb-4">Role Responsibilities</h3>
                <div className="text-slate-700 text-[15px] leading-relaxed font-medium whitespace-pre-wrap">
                  {job.responsibilities}
                </div>
              </div>
            ) : null}

            {job.skills ? (
              <div className="pt-8">
                <h3 className="text-base font-black text-slate-800 mb-4">Essential Skills</h3>
                <div className="text-slate-700 text-[15px] leading-relaxed font-medium whitespace-pre-wrap">
                  {job.skills}
                </div>
              </div>
            ) : null}
          </div>

          <aside className="lg:col-span-4 bg-slate-100/80 p-8 lg:p-10">
            <div className="space-y-5">
              {job.job_code ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Job Code</p>
                  <p className="text-slate-800 text-sm font-bold">{job.job_code}</p>
                </div>
              ) : null}
              {job.manager ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Recruitment Manager</p>
                  <p className="text-slate-800 text-sm font-bold">{job.manager}</p>
                </div>
              ) : null}
              {job.experience_level ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Experience Level</p>
                  <p className="text-slate-800 text-sm font-bold">{job.experience_level}</p>
                </div>
              ) : null}
              {job.experience ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Years of Experience</p>
                  <p className="text-slate-800 text-sm font-bold">{job.experience}</p>
                </div>
              ) : null}
              {job.tags ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Primary Skills</p>
                  <p className="text-slate-800 text-sm font-bold leading-relaxed">{job.tags}</p>
                </div>
              ) : null}
              {job.type ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Employment Type</p>
                  <p className="text-slate-800 text-sm font-bold">{job.type}</p>
                </div>
              ) : null}
              {job.work_auth ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Work Authorization</p>
                  <p className="text-slate-800 text-sm font-bold">{job.work_auth}</p>
                </div>
              ) : null}
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Job Status</p>
                <p className="text-slate-800 text-sm font-bold">{job.status === 'inactive' ? 'Inactive' : 'Active'}</p>
              </div>
              {job.location ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">States</p>
                  <p className="text-slate-800 text-sm font-bold">{job.location}</p>
                </div>
              ) : null}
              {job.salary ? (
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-500 mb-1">Pay Rate / Salary</p>
                  <p className="text-slate-800 text-sm font-bold">{job.salary}</p>
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      </div>
    </div>
    </>
  );
};

export default JobDetailPage;
